import { For, Match, Show, Switch, createEffect, createMemo, createSignal, on, onMount, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Grid } from '../../grid-system/grid/grid';
import { TopCategory, eventsStore, setAvailableCategories, setAvailableLanguages, setAvailableTags, setStatus } from '../../stores/events-store';
import { ErrorCatcher } from '../../tools/error-catcher';
import { ViewStyleButton } from '../../ui-components/view-style-button/view-style-button';
import { EventListCardData, Tag } from '../event/event-types';
import { EventsContainerProps } from './events-types';
import { extractTopCategories } from './helpers/categories-helper';
import { createGroupedEventCard } from './helpers/events-card-helper';
import { getUniqueLanguages, languageCodes } from './helpers/languages-helper';
import { getUniqueTags } from './helpers/tags-helper';
import { ListViewEventCard } from './list-view-event-card';
import { isPastEvent } from './helpers/is-past-event';
import { isOnDemandEvent } from './helpers/is-on-demand-event';
import { isInPersonEvent } from './helpers/is-in-person-event';
import { statusNameToStatusTranslated } from './helpers/translations-helper';
import { ItemListSchema, mapEventsToItemList } from '../structured-data';

export const EventsContainer = (props: EventsContainerProps) => {
    const { localize } = useContext(AppContext);

    const [viewStyle, setViewStyle] = createSignal<string>('gallery');

    // When opening page check if there are upcoming events. If yes show them, if no show all events.
    // But only if user didn't land on the page with search params, as this means the
    // selected filters are intentional and should be respected.
    onMount(() => {
        if (eventsStore.landedWithSearchParams) {
            return;
        }

        // Default status is 'upcoming', only change to 'all' if there are no upcoming events
        const upcomingEvents = filterEvents();

        if (upcomingEvents.length === 0) {
            setStatus({ name: 'All', slug: 'all' });
        }
    });

    const breadcrumbs = () => `${localize('events', 'Events')} ${eventsStore.status?.name ? `/ ${statusNameToStatusTranslated(eventsStore.status?.name, localize, props.labels)}` : ''}`;

    const filterEvents = () => {
        const propertyFiltered = filterByProperties();
        const statusFiltered = filterTemporally(propertyFiltered);
        return statusFiltered;
    };

    const filterTemporally = (events: EventListCardData[]) => {
        const status = eventsStore.status?.slug || 'all';

        const onDemandEvents: EventListCardData[] = [];
        const pastEvents: EventListCardData[] = [];
        const upcomingEvents: EventListCardData[] = [];
        const inPersonEvents: EventListCardData[] = [];

        events.forEach((event: EventListCardData) => {
            if (isOnDemandEvent(event)) {
                onDemandEvents.push(event);
            } else if (isPastEvent(event)) {
                pastEvents.push(event);
            } else {
                upcomingEvents.push(event);
                if (isInPersonEvent(event)) {
                    inPersonEvents.push(event);
                }
            }
        });

        if (status === 'in-person') {
            return inPersonEvents;
        }

        if (status === 'on-demand') {
            return onDemandEvents.reverse();
        }

        if (status === 'all') {
            return [
                ...upcomingEvents,
                ...onDemandEvents.reverse(),
                ...pastEvents.reverse(),
            ];
        }

        if (status === 'upcoming') {
            return [
                ...upcomingEvents,
                ...onDemandEvents.reverse(),
            ];
        }

        if (status === 'past') {
            return pastEvents.reverse();
        }

        return [];
    };

    const filterByProperties = createMemo(() => {
        const areaOfCare = eventsStore.areaOfCare?.slug || 'all';

        const filtered = props?.events
            ?.filter((event: EventListCardData) => {
                // "Unlisted" events are hidden from the hub on every site, but
                // remain reachable via their direct link (and searchable).
                if (event?.hideFromEventsListing) {
                    return false;
                }
                return true;
            })
            ?.filter((event: EventListCardData) => {
                if (!props.isInternationalSite) return true;

                if (event?.hideFromGlobalEventsListing) {
                    return false;
                }
                return true;
            })
            ?.filter((event: EventListCardData) => {
                if (areaOfCare === 'all') {
                    return true;
                }

                if (Array.isArray(event.areaOfCare)) {
                    // event.areaOfCare is now an array
                    return event.areaOfCare.includes(areaOfCare);
                }
                
                // but it used to be a string, so we need to check for both for legacy reasons
                return areaOfCare === event.areaOfCare;
            })
            ?.filter((event: EventListCardData) => {
                if (eventsStore?.selectedCategories?.length === 0) {
                    return true;
                }
                const allCategorySlugs = eventsStore?.selectedCategories?.map((category: TopCategory) => category.slug);
                return !!event?.tags?.find((tag: string) => allCategorySlugs.includes(tag));
            })
            ?.filter((event: EventListCardData) => {
                if (eventsStore?.selectedTags?.length === 0) {
                    return true;
                }
                const allTagSlugs = eventsStore?.selectedTags?.map((tag: Tag) => tag.slug);
                return event?.wordpressTags?.find((tag: Tag) => allTagSlugs.includes(tag.slug));
            })
            ?.filter((event: EventListCardData) => {
                if (eventsStore?.selectedLanguages?.length === 0) {
                    return true;
                }
                return eventsStore?.selectedLanguages?.includes(languageCodes[event?.eventLanguage || '']);
            });
            
        return filtered;
    });

    const filteredEvents = () => filterEvents();

    const itemListItems = () => mapEventsToItemList(filteredEvents() as any);

    createEffect(() => {
        const extractedUniqueLanguages = getUniqueLanguages(props.events);
        setAvailableLanguages(extractedUniqueLanguages);
    });

    const extractAndSetCategoriesAndTags = () => {
        const extractedTopCategories = extractTopCategories(filteredEvents());
        setAvailableCategories(extractedTopCategories);

        const extractedEventTags = getUniqueTags(filteredEvents());
        setAvailableTags(extractedEventTags);
    };
    extractAndSetCategoriesAndTags();

    createEffect(
        on(
            () => filteredEvents(),
            () => extractAndSetCategoriesAndTags()
        )
    );

    /* List view: dated events in one run (the eyebrow carries the date, so no month
       headings), on-demand events under their own heading. */
    const listViewGroupedEvents = createMemo(() => {
        const dated: EventListCardData[] = [];
        const onDemand: EventListCardData[] = [];

        filteredEvents().forEach((event) => {
            if (isOnDemandEvent(event)) {
                onDemand.push(event);
            } else if (event?.eventDate || event?.date) {
                dated.push(event);
            }
        });

        return { dated, onDemand };
    });

    return (
        <ErrorCatcher componentName="Events container">
            <ItemListSchema
                items={itemListItems()}
                listOrder="ItemListOrderAscending"
                name={localize('events', 'Events')}
            />
            <div class="pb-cvi-section-default tablet-original:pt-cvi-section-close">
                <h2 class="m-0 mb-cvi-6 hidden heading-xl font-cvi-medium text-cvi-fg-default tablet:block">
                    {breadcrumbs()}
                </h2>

                <div role="group" aria-label={localize('events-view-toggle', 'Change view')} class="flex flex-wrap gap-cvi-3">
                    <ViewStyleButton
                        text={props.labels?.listView || 'List view'}
                        type="list"
                        onClick={() => setViewStyle('list')}
                        isActive={viewStyle() === 'list'}
                    />
                    <ViewStyleButton
                        text={props.labels?.galleryView || 'Gallery view'}
                        type="gallery"
                        onClick={() => setViewStyle('gallery')}
                        isActive={viewStyle() === 'gallery'}
                    />
                </div>

                <div class="mt-cvi-8">
                    <Switch>
                        <Match when={viewStyle() === 'gallery'}>
                            <Grid templateShorthand={[4, 4, 4]} responsive={{ mobile: [12], tablet: [12], smallDesktop: [6, 6] }} inheritParentGrid={true} customCss='row-gap: var(--spacing-cvi-11)'>
                                <For each={filteredEvents()}>
                                    {(event: EventListCardData) => createGroupedEventCard({ event })}
                                </For>
                            </Grid>
                        </Match>

                        <Match when={viewStyle() === 'list'}>
                            <Show when={listViewGroupedEvents().dated.length > 0}>
                                <ul class="m-0 list-none p-0">
                                    <For each={listViewGroupedEvents().dated}>{(event) => (
                                        <ListViewEventCard event={event} />
                                    )}</For>
                                </ul>
                            </Show>

                            <Show when={listViewGroupedEvents().onDemand.length > 0}>
                                <h3 class="m-0 mt-cvi-11 mb-cvi-4 heading-lg font-cvi-medium text-cvi-fg-default first:mt-0">
                                    {localize('on-demand', 'On demand')}
                                </h3>
                                <ul class="m-0 list-none p-0">
                                    <For each={listViewGroupedEvents().onDemand}>{(event) => (
                                        <ListViewEventCard event={event} />
                                    )}</For>
                                </ul>
                            </Show>
                        </Match>
                    </Switch>

                    <Show when={filteredEvents().length === 0 && listViewGroupedEvents().onDemand.length === 0}>
                        <p class="m-0 body-md font-cvi-regular text-cvi-fg-muted">
                            {localize('no-events-found', 'No events found...')}
                        </p>
                    </Show>
                </div>
            </div>
        </ErrorCatcher>
    );
};
